import React, { useState, useCallback } from "react";
import {
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import { PlayArrow, Add, Delete, LinearScale } from "@material-ui/icons";
import ReactJson from "react-json-view";
import { toast } from "react-toastify";
import {
  runHttpRequestTest,
  formatHttpTestError,
} from "../utils/httpRequestTest";

const useStyles = makeStyles(() => ({
  testBtn: {
    backgroundColor: "#2e7d32",
    color: "#fff",
    textTransform: "none",
    marginTop: 8,
    marginBottom: 8,
    "&:hover": { backgroundColor: "#1b5e20" },
  },
  responseBox: {
    maxHeight: 160,
    overflow: "auto",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: 8,
    backgroundColor: "#fafafa",
    marginTop: 8,
  },
  mappingRow: {
    display: "flex",
    gap: 6,
    alignItems: "flex-start",
    marginBottom: 6,
  },
  mappingField: {
    flex: 1,
  },
  tipBox: {
    marginTop: 12,
    padding: 10,
    borderRadius: 6,
    backgroundColor: "#fff8e1",
    border: "1px solid #ffe082",
    fontSize: 11,
    color: "#5d4037",
    lineHeight: 1.45,
  },
  toggleBtn: {
    textTransform: "none",
    marginTop: 8,
    marginBottom: 4,
    borderColor: "#673ab7",
    color: "#673ab7",
  },
}));

export default function HttpRequestTestPanel({ data, onUpdateData }) {
  const classes = useStyles();
  const [testing, setTesting] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [showMapping, setShowMapping] = useState(
    () => (data?.saveVariables || []).length > 0
  );

  const saveVariables = Array.isArray(data?.saveVariables)
    ? data.saveVariables
    : [];

  const updateSaveVariables = useCallback(
    (next) => {
      const responseVariables = next
        .filter((v) => v?.path && v?.variable)
        .map((v) => ({ path: v.path, variableName: v.variable }));
      onUpdateData({
        saveVariables: next,
        responseVariables,
      });
    },
    [onUpdateData]
  );

  const handleTest = async () => {
    if (!data?.url?.trim()) {
      toast.warn("Informe a URL antes de testar.");
      return;
    }
    setTesting(true);
    setResponse(null);
    setError(null);
    try {
      const result = await runHttpRequestTest({
        url: data.url,
        method: data.method,
        headersString: data.headersString,
        requestBody: data.requestBody,
        queryParams: data.queryParams,
        timeout: data.timeout,
        saveVariables,
      });
      setResponse(result.data);
      if (result.responseVariables?.length) {
        onUpdateData({
          responseVariables: result.responseVariables,
          saveVariables,
        });
      }
      toast.success("Requisição executada com sucesso!");
    } catch (err) {
      const errData = formatHttpTestError(err);
      setError(errData);
      if (window.setFlowVariable) {
        window.setFlowVariable("apiError", errData);
      }
      toast.error("Falha ao testar a requisição.");
    } finally {
      setTesting(false);
    }
  };

  const addVariable = () => {
    updateSaveVariables([...saveVariables, { path: "", variable: "" }]);
    setShowMapping(true);
  };

  const updateVariable = (index, field, value) => {
    const next = saveVariables.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    updateSaveVariables(next);
  };

  const removeVariable = (index) => {
    updateSaveVariables(saveVariables.filter((_, i) => i !== index));
  };

  const displayData = error ?? response;

  return (
    <>
      <Button
        variant="contained"
        className={classes.testBtn}
        startIcon={testing ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
        onClick={handleTest}
        disabled={testing || !data?.url?.trim()}
        fullWidth
      >
        {testing ? "Testando..." : "Testar Requisição"}
      </Button>

      {displayData != null && (
        <div>
          <Typography variant="caption" style={{ fontWeight: 700 }}>
            {error ? "Erro da API:" : "Resposta da API:"}
          </Typography>
          <div className={classes.responseBox}>
            {typeof displayData === "object" ? (
              <ReactJson
                src={displayData}
                theme="rjv-default"
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={false}
                collapsed={1}
                name={false}
              />
            ) : (
              <Typography variant="body2" style={{ fontFamily: "monospace", fontSize: 12 }}>
                {String(displayData)}
              </Typography>
            )}
          </div>
        </div>
      )}

      <Button
        variant="outlined"
        size="small"
        className={classes.toggleBtn}
        startIcon={<LinearScale />}
        onClick={() => setShowMapping((v) => !v)}
        fullWidth
      >
        {showMapping ? "Ocultar" : "Mapear"} Variáveis da Resposta
      </Button>

      {showMapping && (
        <div style={{ marginTop: 8 }}>
          <Typography variant="caption" style={{ fontWeight: 700, color: "#673ab7" }}>
            Mapear dados da resposta para variáveis
          </Typography>
          {saveVariables.map((row, index) => (
            <div key={index} className={classes.mappingRow}>
              <TextField
                className={classes.mappingField}
                label="Caminho"
                variant="outlined"
                size="small"
                value={row.path || ""}
                onChange={(e) => updateVariable(index, "path", e.target.value)}
                placeholder="title"
              />
              <TextField
                className={classes.mappingField}
                label="Variável"
                variant="outlined"
                size="small"
                value={row.variable || ""}
                onChange={(e) => updateVariable(index, "variable", e.target.value)}
                placeholder="title"
              />
              <IconButton size="small" onClick={() => removeVariable(index)}>
                <Delete fontSize="small" />
              </IconButton>
            </div>
          ))}
          <Button
            size="small"
            startIcon={<Add />}
            onClick={addVariable}
            style={{ textTransform: "none", color: "#673ab7" }}
          >
            Adicionar variável
          </Button>
        </div>
      )}

      <div className={classes.tipBox}>
        <strong>Dica:</strong> a resposta completa fica em <code>apiResponse</code>.
        Use <code>${"{"}nomeDaVariavel{"}"}</code> na URL e no body. Nos blocos de mensagem
        use <code>${"{"}variavel.campo{"}"}</code>.
      </div>
    </>
  );
}
